import { FaceComparisonPayload } from '@hakimo-ui/hakimo/util';
import { Label } from '@hakimo-ui/shared/ui-base';
import { useMemo } from 'react';
import { isBadgeProfile } from '../../matched-faces/util';
import { CroppedImage } from './CroppedImage';
import { ReferenceImage } from './ReferenceImage';

export function HeaderStrip({ payload }: { payload: FaceComparisonPayload }) {
  const formattedTime = useMemo(() => {
    if (!payload.timestamp) return null;
    try {
      return new Date(payload.timestamp).toLocaleString();
    } catch {
      return null;
    }
  }, [payload.timestamp]);

  const isBadge = isBadgeProfile(payload.profileType);
  const idLabel = isBadge ? 'BADGE ID' : 'EMP ID';
  const idValue = isBadge ? payload.badgeId : payload.employeeId;

  return (
    <div className="border-onlight-line-2 dark:border-ondark-line-2 mb-4 flex flex-wrap items-center gap-3 border-b pb-3">
      <span className="text-onlight-text-1 dark:text-ondark-text-1 text-lg font-semibold">
        {payload.profileName}
      </span>
      {idValue && (
        <span className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
          {idLabel}: {idValue}
        </span>
      )}
      {payload.gender && <Label type="default" text={payload.gender} small />}
      {formattedTime && (
        <span className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
          {formattedTime}
        </span>
      )}
      {payload.cameraName && (
        <span className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
          {payload.cameraName}
        </span>
      )}
      {payload.alarmType && (
        <span className="bg-onlight-bg-3 dark:bg-ondark-bg-3 text-onlight-text-1 dark:text-ondark-text-1 inline-flex items-center rounded-2xl px-3.5 py-0.5 text-base font-semibold">
          {payload.alarmType}
        </span>
      )}
    </div>
  );
}

export function Cell({
  subLabel,
  children,
}: {
  subLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-col gap-1">
      <span className="text-onlight-text-2 dark:text-ondark-text-2 text-xs font-medium uppercase leading-4 tracking-wide">
        {subLabel || '\u00A0'}
      </span>
      <div className="min-h-0 flex-1 overflow-hidden rounded">{children}</div>
    </div>
  );
}

export function ComparisonGrid({
  payload,
}: {
  payload: FaceComparisonPayload;
}) {
  const isBadge = isBadgeProfile(payload.profileType);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="grid grid-cols-2 gap-x-6">
        <h2 className="text-onlight-text-1 dark:text-ondark-text-1 text-base font-semibold">
          Reference Image
        </h2>
        <h2 className="text-onlight-text-1 dark:text-ondark-text-1 text-base font-semibold">
          Detected Person
        </h2>
      </div>
      {isBadge ? (
        <div className="grid min-h-0 flex-1 grid-cols-2 gap-x-6">
          <div className="min-h-0 overflow-hidden rounded">
            <ReferenceImage
              src={payload.referencePrimaryUrl}
              alt={`${payload.profileName} reference`}
              placeholder="Reference image unavailable"
            />
          </div>
          <div className="min-h-0 overflow-hidden rounded">
            <CroppedImage
              src={payload.frameUrl}
              box={payload.personBox}
              alt={`${payload.profileName} detected person`}
              placeholder="Detected person unavailable"
            />
          </div>
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-2 grid-rows-[40%_60%] gap-x-6 gap-y-4">
          <Cell subLabel="Face">
            <ReferenceImage
              src={payload.referencePrimaryUrl}
              alt={`${payload.profileName} primary reference`}
              placeholder="Reference face unavailable"
            />
          </Cell>
          <Cell>
            <CroppedImage
              src={payload.frameUrl}
              box={payload.faceBox}
              alt={`${payload.profileName} detected face`}
              placeholder="Detected face unavailable"
            />
          </Cell>
          <Cell subLabel="Full Body">
            <ReferenceImage
              src={payload.referenceFullBodyUrl ?? undefined}
              alt={`${payload.profileName} full body reference`}
              placeholder="Full body reference unavailable"
            />
          </Cell>
          <Cell>
            <CroppedImage
              src={payload.frameUrl}
              box={payload.personBox}
              alt={`${payload.profileName} detected body`}
              placeholder="Detected full body unavailable"
            />
          </Cell>
        </div>
      )}
    </div>
  );
}
